import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../ui/textarea";
import { useCreateWriting } from "@/api/writing/writing";
import {
  appendNoDuplicateRecordNumberRefine,
  writingSchema,
} from "@/api/writing/writing-validation";

const schema = appendNoDuplicateRecordNumberRefine(
  writingSchema.pick({
    ecritsDescriptionNumeroEnregistrement: true,
    ecritsDescriptionTitre: true,
  }),
);
type FormType = z.infer<typeof schema>;

type NewWritingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewWritingDialog = ({ onOpenChange, open }: NewWritingDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: {
      ecritsDescriptionNumeroEnregistrement: "",
      ecritsDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreateWriting();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType) => {
    try {
      const result = await mutateAsync(data);
      navigate(`/edit/writing/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("ecritsDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createWwriting")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="ecritsDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>
              {t("ecritsDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="ecritsDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("ecritsDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewWritingDialog;
